"use client";

import Link, { LinkProps } from "next/link";
import { buildOrgPath } from "@/lib/navigation-utils";
import { useOrganization } from "@/hooks/queries";
import { useAppVariant } from "@/components/app-variant-context";

interface AppLinkProps extends Omit<LinkProps, "href"> {
  href: string;
  children: React.ReactNode;
  className?: string;
  target?: string;
  rel?: string;
  title?: string;
}

/**
 * Simple Link that uses valid org_slug from TanStack Query cache.
 * Blocks navigation while the org slug is not yet available (e.g. right after login)
 * to prevent landing on routes without the org prefix.
 *
 * In the "admin" app variant the org prefix is not applied (admin URLs are at
 * the root, like /dashboard) and the organization query is skipped.
 */
export function AppLink({ href, ...props }: AppLinkProps) {
  const variant = useAppVariant();
  const isAdmin = variant === "admin";

  const { data: organization } = useOrganization({ enabled: !isAdmin });

  if (isAdmin) {
    return <Link href={href} {...props} />;
  }

  if (!organization?.urlSlug) {
    return (
      <Link
        href="#"
        {...props}
        onClick={(e) => e.preventDefault()}
        aria-disabled
      />
    );
  }

  return <Link href={buildOrgPath(organization.urlSlug, href)} {...props} />;
}

export default AppLink;
